import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GitBranch } from 'lucide-react';
import { fetchApi } from '../config/api';
import { Alert, Loading, PageBackdrop, PageHeader, Select, SegmentedTabs } from '../components/UI';
import { PedigreeTree, type PedigreeNode } from '../components/PedigreeTree';

const GENERATIONS = 4;

interface BreedOption {
  breedId: number;
  breedNameGerman: string;
  breedNameEnglish: string;
  dogCount: number;
}

interface DogOption {
  id: number;
  name: string;
  sex: string;
  dateOfBirth: string | null;
  knownAncestorCount: number;
}

const unwrap = (res: any) => res?.data ?? res;

export default function Pedigree() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [breeds, setBreeds] = useState<BreedOption[]>([]);
  const [breedsLoading, setBreedsLoading] = useState(true);
  const [breedsError, setBreedsError] = useState('');

  const [dogs, setDogs] = useState<DogOption[]>([]);
  const [dogsLoading, setDogsLoading] = useState(false);

  const [tree, setTree] = useState<PedigreeNode | null>(null);
  const [treeLoading, setTreeLoading] = useState(false);
  const [treeError, setTreeError] = useState('');

  const selectedBreedId = Number(searchParams.get('breed')) || null;
  const selectedDogId = Number(searchParams.get('dog')) || null;

  // Verfuegbare Rassen laden; ohne Auswahl in der URL automatisch die erste (meist einzige) Rasse waehlen.
  useEffect(() => {
    let cancelled = false;
    setBreedsLoading(true);
    setBreedsError('');
    fetchApi('/pedigree/breeds')
      .then((res) => {
        if (cancelled) return;
        const list: BreedOption[] = unwrap(res) ?? [];
        setBreeds(list);
        if (list.length > 0 && !list.some((b) => b.breedId === selectedBreedId)) {
          setSearchParams({ breed: String(list[0].breedId) }, { replace: true });
        }
      })
      .catch((err) => {
        if (!cancelled) setBreedsError(err?.message || t('pedigree:error'));
      })
      .finally(() => {
        if (!cancelled) setBreedsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hunde der ausgewaehlten Rasse laden; ohne Hund-Auswahl in der URL den mit den meisten bekannten Vorfahren waehlen.
  useEffect(() => {
    if (!selectedBreedId) {
      setDogs([]);
      return;
    }
    let cancelled = false;
    setDogsLoading(true);
    fetchApi(`/pedigree/breeds/${selectedBreedId}/dogs`)
      .then((res) => {
        if (cancelled) return;
        const list: DogOption[] = unwrap(res) ?? [];
        setDogs(list);
        if (list.length > 0 && !list.some((d) => d.id === selectedDogId)) {
          setSearchParams({ breed: String(selectedBreedId), dog: String(list[0].id) }, { replace: true });
        }
      })
      .catch(() => setDogs([]))
      .finally(() => {
        if (!cancelled) setDogsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBreedId]);

  // Baum fuer den ausgewaehlten Hund laden.
  useEffect(() => {
    if (!selectedDogId) {
      setTree(null);
      return;
    }
    let cancelled = false;
    setTreeLoading(true);
    setTreeError('');
    fetchApi(`/pedigree/dogs/${selectedDogId}/tree?generations=${GENERATIONS}`)
      .then((res) => {
        if (!cancelled) setTree(unwrap(res)?.root ?? null);
      })
      .catch((err) => {
        if (!cancelled) {
          setTree(null);
          setTreeError(err?.message || t('pedigree:error'));
        }
      })
      .finally(() => {
        if (!cancelled) setTreeLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDogId]);

  const selectBreed = (breedId: number) => setSearchParams({ breed: String(breedId) });
  const selectDog = (dogId: number) => setSearchParams({ breed: String(selectedBreedId), dog: String(dogId) });

  return (
    <>
      <PageBackdrop />
      <div className="relative z-10 container mx-auto px-4 py-8 sm:px-6 sm:py-10">
        <PageHeader
          title={t('pedigree:title')}
          backLink={{ label: t('nav:primary.cockpit'), onClick: () => navigate('/dashboard') }}
        />

        {breedsLoading ? (
          <Loading />
        ) : breedsError ? (
          <Alert type="error" message={breedsError} />
        ) : breeds.length === 0 ? (
          <Alert type="info" message={t('pedigree:noData')} />
        ) : (
          <div className="space-y-4">
            {breeds.length > 1 && (
              <SegmentedTabs
                tabs={breeds.map((b) => ({ id: String(b.breedId), label: b.breedNameGerman, icon: <GitBranch size={14} /> }))}
                value={selectedBreedId ? String(selectedBreedId) : ''}
                onChange={(id) => selectBreed(Number(id))}
              />
            )}

            {dogsLoading ? (
              <Loading />
            ) : dogs.length === 0 ? (
              <Alert type="info" message={t('pedigree:noDogs')} />
            ) : (
              <>
                {dogs.length > 1 && (
                  <Select
                    label={t('pedigree:selectDog')}
                    value={selectedDogId ? String(selectedDogId) : ''}
                    onChange={(e) => selectDog(Number(e.target.value))}
                    options={dogs.map((d) => ({ value: String(d.id), label: d.name }))}
                    fullWidth={false}
                    className="max-w-xs"
                  />
                )}

                <div className="card">
                  {treeLoading ? (
                    <Loading />
                  ) : treeError ? (
                    <Alert type="error" message={treeError} />
                  ) : tree ? (
                    <PedigreeTree root={tree} generations={GENERATIONS} />
                  ) : null}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}
